import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, mergeMap, of, retry, switchMap, tap, timer } from 'rxjs';

import { environment } from '@environments/environment';
import { CommonRequestService } from './common-request.service';
import { StateManagerProjectService } from '@state-manager';

@Injectable()
export class DataRequestService extends CommonRequestService {
  private readonly _examplesPath = './assets/examples-dcm/';

  constructor(
    private _http: HttpClient,
    private _route: ActivatedRoute,
    private _stateManagerProjectService: StateManagerProjectService
  ) {
    super();
  }

  /**
   * Request to server to get nrrd image file
   * @method getNrrdImage
   * @param {string} caseId Case ID
   * @return {Observable<Blob>} Blob for nrrd file as Observable
   */
  public getNrrdImage(caseId: string): Observable<Blob> {
    if (!environment.production) {
      return this._http.get(
        `${this._examplesPath}/${this._getExampleFileName(
          this._route.snapshot.queryParams.fileId
        )}`,
        {
          responseType: 'blob',
        }
      );
    }

    return this._httpGetBlobRequest(
      `${this._url}/rawnrrdimage`,
      caseId,
      'rawNrrdImageLink'
    );
  }

  /**
   * Request to server to get nrrd original mask file
   * @method getNrrdOriginalMask
   * @param {string} caseId Case ID
   * @return {Observable<Blob>} Blob for nrrd file as Observable
   */
  public getNrrdOriginalMask(caseId: string): Observable<Blob> {
    if (!environment.production) {
      return this._http.get(
        `${this._examplesPath}/${this._getExampleFileName(
          this._route.snapshot.queryParams.fileId,
          true
        )}`,
        {
          responseType: 'blob',
        }
      );
    }

    return this._httpGetBlobRequest(
      `${this._url}/segmentednrrdmask`,
      caseId,
      'segmentedNrrdMaskLink'
    );
  }

  /**
   * Request to server to get nrrd updated mask file
   * @method getNrrdUpdatedMask
   * @param {string} caseId Case ID
   * @return {Observable<Blob>} Blob for nrrd file as Observable
   */
  public getNrrdUpdatedMask(
    caseId: string,
    cb?: Function
  ): Observable<Blob | null> {
    if (!environment.production) {
      return of(null).pipe(tap((_) => cb?.(false)));
    }

    return this._http
      .get<{ isFileAvailable: boolean; draftSegmentedNrrdMaskLink: string }>(
        `${this._url}/draftsegmentednrrdmask`,
        {
          params: {
            caseId: caseId,
          },
        }
      )
      .pipe(
        tap((res) => cb?.(res.isFileAvailable)),
        switchMap((res) =>
          res.isFileAvailable
            ? this._http.get(res.draftSegmentedNrrdMaskLink, {
                responseType: 'blob',
              })
            : of(null)
        )
      );
  }

  /**
   * Request to server to get 3d bone model file
   * @method getBoneModel
   * @param {string} caseId Case ID
   * @return {Observable<Blob>} Blob for stl file as Observable
   */
  public getBoneModel(caseId: string): Observable<Blob> {
    if (!environment.production) {
      const fileName = 'modelTablet.stl';
      return this._http.get(`${this._examplesPath}/d3-models/${fileName}`, {
        responseType: 'blob',
      });
    }

    return this._httpGetBlobRequest(
      `${this._url}/rawbonemodel`,
      caseId,
      'raw3DBoneModelLink'
    );
  }

  /**
   * Request to server to get updated 3d bone model file
   * @method getUpdatedBoneModel
   * @param {string} caseId Case ID
   * @return {Observable<Blob>} Blob for stl file as Observable
   */
  public getUpdatedBoneModel(
    caseId: string,
    requestDelay: number
  ): Observable<Blob | null> {
    if (!environment.production) {
      const fileName = 'modelTablet.stl';
      return this._http.get(`${this._examplesPath}/d3-models/${fileName}`, {
        responseType: 'blob',
      });
    }

    return this._httpGetBlobIfAvailableRequest(
      `${this._url}/draftbonemodel`,
      caseId,
      'draft3DBoneModelLink',
      requestDelay
    );
  }

  /**
   * Request to server to get temporary 3d bone model file
   * @method getTemporaryBoneModel
   * @param {string} caseId Case ID
   * @return {Observable<Blob>} Blob for stl file as Observable
   */
  public getTemporaryBoneModel(
    caseId: string,
    requestDelay: number
  ): Observable<Blob | null> {
    if (!environment.production) {
      const fileName = 'modelTablet.stl';
      return this._http.get(`${this._examplesPath}/d3-models/${fileName}`, {
        responseType: 'blob',
      });
    }

    return this._httpGetBlobIfAvailableRequest(
      `${this._url}/draftinsessionbonemodel`,
      caseId,
      'draftInSession3DBoneModelLink',
      requestDelay
    );
  }

  /**
   * Request to server to to save changes in nrrd mask
   * @method saveNrrdMask
   * @param {string} caseId Case ID
   * @param {File} nrrdFile NRRD mask file
   * @param {boolean} isApproved saving the mask before or after the approval
   * @param {boolean} isSessionSave saving the mask within the session or not
   */
  public saveNrrdMask(
    caseId: string,
    nrrdFile: File,
    isApproved: boolean,
    isSessionSave: boolean
  ): Observable<void> {    
    if (!environment.production) {
      return of(undefined);
    }

    return this._http
      .get<{ segmentedNrrdMaskUploadLink: string }>(
        `${this._url}/savedraftsegmentednrrdmask`,
        {
          params: {
            caseId,
            isApproved,
            isSessionSave,
          },
        }
      )
      .pipe(
        switchMap((res) => {
          const headers = new HttpHeaders({
            'Content-Type': 'multipart/form-data',
          });
          return this._http.put<void>(
            res.segmentedNrrdMaskUploadLink,
            nrrdFile,
            { headers: headers }
          );
        })
      );
  }

  public saveNrrdMasks(
    caseId: string,
    nrrdFile: File,
    isApproved: boolean,
    isSessionSave: boolean
  ): Observable<void> {
    return this._http
      .get<{ segmentedNrrdMaskUploadLink: string }>(
        `${this._url}/savedraftsegmentednrrdmask`,
        {
          params: {
            caseId,
            isApproved,
            isSessionSave,
          },
        }
      )
      .pipe(
        switchMap((res) => {
          const headers = new HttpHeaders({
            'Content-Type': 'multipart/form-data',
          });
          return this._http.put<void>(
            res.segmentedNrrdMaskUploadLink,
            nrrdFile,
            { headers: headers }
          );
        })
      );
  }

  /**
   * Request to the server to generate a 3d model
   * @method generateBoneModel
   * @param {string} caseId Case ID
   * @param {boolean} isApproved model generation before or after approval
   * @param {boolean} isSessionSave model generation within the session or not
   */
  public generateBoneModel(
    caseId: string,
    isApproved: boolean,
    isSessionSave: boolean
  ): Observable<void> {
    if (!environment.production) {
      return of(undefined);
    }

    return this._http.put<void>(`${this._url}/generatebonemodel`, {
      caseId,
      isApproved,
      isSessionSave,
    });
  }

  public generateBoneModels(
    caseId: string,
    isApproved: boolean,
    isSessionSave: boolean
  ): Observable<void> {
    return this._http.put<void>(`${this._url}/generatebonemodel`, {
      caseId,
      isApproved,
      isSessionSave,
    });
  }

  private _httpGetBlobRequest(
    url: string,
    caseId: string,
    getFileUrlProperty: string
  ): Observable<Blob> {
    return this._http
      .get(url, {
        params: {
          caseId: caseId,
        },
      })
      .pipe(
        switchMap((res) =>
          this._http.get(res[getFileUrlProperty], {
            responseType: 'blob',
          })
        )
      );
  }

  private _httpGetBlobIfAvailableRequest(
    url: string,
    caseId: string,
    getFileUrlProperty: string,
    requestDelay: number
  ): Observable<Blob | null> {
    // PRSAMD-4460: delay was introduced to fix this issue. Without delay old file is coming from BE
    return timer(requestDelay).pipe(
      mergeMap(() =>
        this._http
          .get<{ isFileAvailable: boolean }>(url, {
            params: {
              caseId: caseId,
            },
          })
          .pipe(
            switchMap((res) => {
              return res.isFileAvailable
                ? this._http
                    .get(res[getFileUrlProperty], {
                      responseType: 'blob',
                    })
                    .pipe(
                      retry({
                        count:
                          this._stateManagerProjectService.requestDelayData
                            .defaultNumberOfAttempts,
                        delay: requestDelay,
                      })
                    )
                : of(null);
            })
          )
      )
    );
  }

  // By adding a query parameter, you can change the files for examples
  // For example: ?fileId=1
  private _getExampleFileName(id: string, isMask = false): string {
    switch (id) {
      case '1':
        return !isMask ? '4NcIhY.nrrd' : '4NcIhY_segmented.nrrd';
      case '2':
        return !isMask ? 'sVz1Lb.nrrd' : 'sVz1Lb_segmented.nrrd';
      default:
        return !isMask ? 'sI93P7.nrrd' : 'sI93P7_segmented.nrrd';
    }
  }
}
